import api from '@/shared/api';
import { handleApiError, withQuery } from '@/shared/lib';
import type {
  CreatePhysicalLocationsPayload,
  PhysicalLocationApiResponse,
  PhysicalLocation,
  PhysicalLocationPageParams,
  PhysicalLocationPageApiResponse,
  PhysicalLocationPageResponse,
  UpdatePhysicalLocationPayload,
  UpdatePhysicalLocationResponse,
} from '../model/types';
import { isAxiosError, type AxiosResponse } from 'axios';

class PhysicalLocationApi {
  async getPhysicalLocations(): Promise<PhysicalLocation[]> {
    try {
      const response: AxiosResponse<PhysicalLocationApiResponse<PhysicalLocation[]>> =
        await api.get('/physical-locations');

      return response.data.data ?? [];
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar localizações físicas');
    }
  }

  async getPhysicalLocationsPage(
    params: PhysicalLocationPageParams
  ): Promise<PhysicalLocationPageResponse> {
    try {
      const query = {
        page: params.page,
        itemsPerPage: params.itemsPerPage,
        term: params.term,
        ...(params.sort ? { sort: params.sort } : {}),
        ...(params.order ? { order: params.order } : {}),
      };

      const response: AxiosResponse<PhysicalLocationPageApiResponse> = await api.get(
        withQuery('/physical-locations', query)
      );

      if (!response.data.metadata) {
        throw new Error('Resposta paginada de localizações físicas sem metadados');
      }

      return {
        items: response.data.data ?? [],
        metadata: response.data.metadata,
      };
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar localizações físicas');
    }
  }

  async createPhysicalLocations(
    payload: CreatePhysicalLocationsPayload
  ): Promise<PhysicalLocation[]> {
    try {
      const response: AxiosResponse<PhysicalLocationApiResponse<PhysicalLocation[]>> =
        await api.post('/physical-locations', payload);

      return response.data.data ?? [];
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        throw error;
      }

      throw handleApiError(error, 'Erro ao criar localizações físicas');
    }
  }

  async updatePhysicalLocation(
    id: number,
    payload: UpdatePhysicalLocationPayload
  ): Promise<UpdatePhysicalLocationResponse> {
    try {
      const response: AxiosResponse<PhysicalLocationApiResponse<UpdatePhysicalLocationResponse>> =
        await api.patch(`/physical-locations/${id}`, payload);

      return response.data.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        throw error;
      }

      throw handleApiError(error, 'Erro ao editar localizações físicas');
    }
  }

  async deletePhysicalLocation(id: number): Promise<void> {
    try {
      await api.delete(`/physical-locations/${id}`);
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        throw error;
      }

      throw handleApiError(error, 'Erro ao excluir localizações físicas');
    }
  }
}

export const physicalLocationApi = new PhysicalLocationApi();
